import BlankSlate from "@pathwright/ui/src/components/blank/BlankSlate"
import Button from "@pathwright/ui/src/components/button/Button"
import Fullscreen, {
  FULLSCREEN_LAYER_OVERLAY
} from "@pathwright/ui/src/components/fullscreen/Fullscreen"
import useCountDown from "@pathwright/ui/src/components/hooks/useCountDown"
import useCohortPermissions from "@pathwright/web/src/modules/cohort/useCohortPermissions"
import { useQuery } from "@pathwright/web/src/modules/utils/apollo"
import get from "lodash/get"
import moment from "moment"
import styled from "styled-components"
import {
  MENTOR_GROUP_MEMBERSHIP_ROLE_ADMIN,
  MENTOR_GROUP_MEMBERSHIP_ROLE_MODERATOR
} from "../../mentor-group/constants"
import { usePathwrightContext } from "../../pathwright/PathwrightContext"
import {
  getIsSubscriptionDelinquent,
  getIsSubscriptionExpired,
  getIsSubscriptionUsable
} from "../../subscription/utils"
import { useUserCommunityContext } from "../../user/UserCommunityContext"
import USER_SCHOOL_SUBSCRIPTION_QUERY from "../../user/graphql/user-school-subscription-query"
import { canEditLibrary } from "../../user/permissions"
import REGISTRATION_EXPIRATION_QUERY from "../graphql/registration-expiration-query"

const Container = styled.div`
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`

const RegistrationExpiredOverlay = ({ cohortId }) => {
  const pwContext = usePathwrightContext()
  const { hasModeratorLevelAccess } = useCohortPermissions({
    cohortId
  })

  const registrationExpirationQuery = useQuery(REGISTRATION_EXPIRATION_QUERY, {
    variables: {
      cohortId
    },
    skip: !cohortId || canEditLibrary(pwContext)
  })

  const {
    communityQuery,
    communityGroupMemberships,
    activeCommunityGroupSubscription,
    communityGroupWithInactiveSubscription,
    inactiveCommunityGroupSubscription
  } = useUserCommunityContext()
  const userSubscriptionQuery = useQuery(USER_SCHOOL_SUBSCRIPTION_QUERY)
  const userSchoolSubscription = get(userSubscriptionQuery, "data.me.school_subscription") // prettier-ignore

  const cohortAccessEndsForceUpdate = useCountDown(
    registrationExpirationQuery.data?.cohort?.access_ends
  )

  if (communityQuery.loading) return null
  if (userSubscriptionQuery.loading) return null
  if (registrationExpirationQuery.loading) return null
  if (!registrationExpirationQuery.data) return null
  if (hasModeratorLevelAccess === null) return null

  const {
    data: { cohort }
  } = registrationExpirationQuery

  // cohort may possibly be null (in edge cases due to licensed resource with public visibility override)
  if (!cohort) return null

  const { user_registration: registration } = cohort

  // no expiration overlay necessary if user does not have registration
  if (!registration) return null

  const cohortRequresPayment =
    !!cohort.price ||
    (cohort.is_subscription_only &&
      (cohort.include_in_plans.total > 0 ||
        cohort.include_with_products.total > 0))

  const viaActiveSchoolSub =
    !!userSchoolSubscription &&
    getIsSubscriptionUsable(userSchoolSubscription.subscription) &&
    cohort.include_in_plans.total > 0 &&
    !!cohort.include_in_plans.edges.find(
      (e) => e.node.id === userSchoolSubscription.school_subscription_plan.id
    )

  const viaActiveGroupSub =
    !!activeCommunityGroupSubscription &&
    getIsSubscriptionUsable(activeCommunityGroupSubscription.subscription) &&
    cohort.include_with_products.total > 0 &&
    !!cohort.include_with_products.edges.find(
      (e) => e.node.id === activeCommunityGroupSubscription.price.product.id
    )

  // assumption here is that if neither a school sub nor a group sub was used to register
  // then the user gained access via purchase or free
  const viaPayment =
    !registration.school_sub_used && !registration.group_sub_used

  const resubActionProps = {
    primaryAction: {
      children: "Subscribe",
      to: "/subscribe/"
    },
    secondaryAction: {
      children: "or Browse Library",
      to: "/library/"
    }
  }

  const renewActionProps = {
    primaryAction: {
      children: "Update Payment Method",
      to: "/manage/profile/billing/payment/"
    }
  }

  const homeActionProps = {
    primaryAction: {
      children: "Go Home",
      to: "/home/"
    }
  }

  // check if the registration is expired
  if (
    registration.expiration_dtime &&
    moment().isAfter(registration.expiration_dtime)
  ) {
    return (
      <Fullscreen layer={FULLSCREEN_LAYER_OVERLAY}>
        <Container>
          <BlankSlate
            inverted
            icon="clock"
            heading="Your registration has expired."
            stack
            {...homeActionProps}
          />
        </Container>
      </Fullscreen>
    )
  }

  // check if the offering access has ended
  if (
    !hasModeratorLevelAccess &&
    cohort.access_ends &&
    moment().isAfter(cohort.access_ends)
  ) {
    return (
      <Fullscreen layer={FULLSCREEN_LAYER_OVERLAY}>
        <Container>
          <BlankSlate
            inverted
            icon="clock"
            heading={`Your access to ${cohort.name} has ended.`}
            stack
            {...homeActionProps}
          />
        </Container>
      </Fullscreen>
    )
  }

  // allow access via any active group sub / school sub or registration via payment
  if (
    !cohortRequresPayment ||
    viaActiveSchoolSub ||
    viaActiveGroupSub ||
    viaPayment
  )
    return null

  const includesInactiveCommunityGroup =
    !!communityGroupWithInactiveSubscription &&
    !!cohort.include_with_products.edges.find(
      (e) => e.node.id === inactiveCommunityGroupSubscription.price.product.id
    )

  const inactiveCommunityGroupMembership = includesInactiveCommunityGroup
    ? communityGroupMemberships.find(
        (cgm) => cgm.group.id === communityGroupWithInactiveSubscription.id
      )
    : null

  // check if user registered via school group subscription
  if (
    includesInactiveCommunityGroup &&
    !getIsSubscriptionUsable(inactiveCommunityGroupSubscription.subscription)
  ) {
    // Only label a Group subscription as "group" if it supports multiple members.
    const isGroup = inactiveCommunityGroupMembership?.group.seat_limit > 1

    const isExpired = getIsSubscriptionExpired(
      inactiveCommunityGroupSubscription.subscription
    )

    const heading =
      [
        "Your",
        isGroup ? "group" : "",
        "subscription is",
        isExpired ? "expired" : "delinquent"
      ]
        .filter(Boolean)
        .join(" ") + "."

    return inactiveCommunityGroupMembership &&
      inactiveCommunityGroupMembership.role in
        [
          MENTOR_GROUP_MEMBERSHIP_ROLE_MODERATOR,
          MENTOR_GROUP_MEMBERSHIP_ROLE_ADMIN
        ] ? (
      <Fullscreen layer={FULLSCREEN_LAYER_OVERLAY}>
        <Container>
          <BlankSlate
            inverted
            icon="recur-x"
            heading={heading}
            // TODO: "and more" might not be accurate
            body={`Subscribe to resume taking ${cohort.resource.name} and more.`}
            stack
            {...(isExpired ? renewActionProps : resubActionProps)}
          />
        </Container>
      </Fullscreen>
    ) : (
      // TODO: prompt mentor group member to do something?
      <Fullscreen layer={FULLSCREEN_LAYER_OVERLAY}>
        <Container>
          <BlankSlate
            inverted
            icon="recur-x"
            heading={heading}
            stack
            {...homeActionProps}
          />
        </Container>
      </Fullscreen>
    )
  }

  // User's group membership has been revoked.
  if (inactiveCommunityGroupMembership?.access_revoked_time) {
    return (
      <Fullscreen layer={FULLSCREEN_LAYER_OVERLAY}>
        <Container>
          <BlankSlate
            inverted
            icon="frown"
            heading={`Your group membership to "${communityGroupWithInactiveSubscription.name}" has been revoked.`}
            stack
            {...homeActionProps}
          />
        </Container>
      </Fullscreen>
    )
  }

  // User's group membership has been archived.
  if (
    registration.group_sub_used &&
    !registration.group_sub_community_membership
  ) {
    return (
      <Fullscreen layer={FULLSCREEN_LAYER_OVERLAY}>
        <Container>
          <BlankSlate
            inverted
            icon="frown"
            heading={`Your group membership to "${registration.group_sub_used.community_group.name}" has been removed.`}
            stack
            {...homeActionProps}
          />
        </Container>
      </Fullscreen>
    )
  }

  // assuming user registered via school subscription which is no longer active
  const isExpired = getIsSubscriptionExpired(
    userSchoolSubscription?.subscription
  )
  const isDelinquent = getIsSubscriptionDelinquent(
    userSchoolSubscription?.subscription
  )

  if (isExpired) {
    return (
      <Fullscreen layer={FULLSCREEN_LAYER_OVERLAY}>
        <Container>
          <BlankSlate
            inverted
            icon="recur-x"
            heading="Your subscription is expired"
            // TODO: "and more" might not be accurate
            body="Subscribe to resume taking ${cohort.resource.name} and more."
            stack
            {...resubActionProps}
          />
        </Container>
      </Fullscreen>
    )
  }

  if (isDelinquent) {
    return (
      <Fullscreen layer={FULLSCREEN_LAYER_OVERLAY}>
        <Container>
          <BlankSlate
            inverted
            icon="recur-x"
            heading="Your subscription is expired."
            body="When attempting to renew your subscription the payment method failed. After multiple attempts, your subscription was cancelled. To restore your subscription access, please update your payment method:"
            stack
            {...renewActionProps}
          />
        </Container>
      </Fullscreen>
    )
  }

  // At this point, if the user used a school subscription to register,
  // it's possible the cohort no longer includes this subscription.
  if (
    registration.school_sub_used &&
    !cohort.include_in_plans?.edges?.some(
      (e) =>
        e.node.id === registration.school_sub_used.school_subscription_plan.id
    )
  ) {
    return (
      <Fullscreen layer={FULLSCREEN_LAYER_OVERLAY}>
        <Container>
          <BlankSlate
            inverted
            icon="recur-x"
            heading="This Cohort is no longer available."
            body={
              <span>
                Your subscription to{" "}
                {registration.school_sub_used.subscription_plan.name} no longer
                grants access to {cohort.name}. Contact{" "}
                <Button
                  color="white"
                  styleType="text"
                  href={`mailto:${pwContext.school.email}`}
                >
                  {pwContext.school.email}
                </Button>{" "}
                for more information.
              </span>
            }
            stack
          />
        </Container>
      </Fullscreen>
    )
  }

  // At this point, if the user used a group subscription to register,
  // it's possible the cohort no longer includes this subscription.
  if (
    registration.group_sub_used &&
    !cohort.include_with_products.edges?.find(
      (e) => e.node.id === registration.group_sub_used.price.product.id
    )
  ) {
    return (
      <Fullscreen layer={FULLSCREEN_LAYER_OVERLAY}>
        <Container>
          <BlankSlate
            inverted
            icon="recur-x"
            heading="This Cohort is no longer available."
            body={
              <span>
                Your subscription to{" "}
                {registration.group_sub_used.price.product.name} no longer
                grants access to {cohort.name}. Contact{" "}
                <Button
                  color="white"
                  styleType="text"
                  href={`mailto:${pwContext.school.email}`}
                >
                  {pwContext.school.email}
                </Button>{" "}
                for more information.
              </span>
            }
            stack
          />
        </Container>
      </Fullscreen>
    )
  }

  // At this point, we're quite certain the user no longer has permissions to access
  // this cohort but we're not sure exactly why.
  // TODO: we're hoping to resolve these access checks via https://linear.app/pathwright/issue/SUP-467/learner-seeing-access-expired-on-completed-paths
  return (
    <Fullscreen layer={FULLSCREEN_LAYER_OVERLAY}>
      <Container>
        <BlankSlate
          inverted
          icon="recur-x"
          heading="This Cohort is no longer available."
          stack
          {...renewActionProps}
        />
      </Container>
    </Fullscreen>
  )
}

RegistrationExpiredOverlay.displayName = "RegistrationExpiredOverlay"

export default RegistrationExpiredOverlay
